import { useRef, useEffect, useCallback } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';

interface UseMessageScrollOptions {
  pagesLength: number;
  messagesLength: number;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}

export const useMessageScroll = ({
  pagesLength,
  messagesLength,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
}: UseMessageScrollOptions) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    if (pagesLength === 1) bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [pagesLength]);

  useEffect(() => {
    if (prevScrollHeightRef.current > 0) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesLength]);

  useEffect(() => {
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop += scrollContainerRef.current.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [isFetchingNextPage]);

  const handleLoadMore = useCallback(() => {
    prevScrollHeightRef.current = scrollContainerRef.current?.scrollHeight ?? 0;
    fetchNextPage();
  }, [fetchNextPage]);

  const [sentinelRef, { rootRef }] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: handleLoadMore,
  });

  const setScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollContainerRef.current = el;
      rootRef(el);
    },
    [rootRef],
  );

  return { bottomRef, setScrollRef, sentinelRef };
};
